import { ReactNode } from 'react';
import { ActionIcon, Badge, Box, Card, Group, LoadingOverlay, Menu, Stack, Text, Title } from '@mantine/core';
import { IconDotsVertical, IconPencil, IconRefresh, IconTrash } from '@tabler/icons-react';
import { MealPlanAssignment, SubstitutionGenerationProgress, getAssignmentMealType } from '../../types/mealPlan';
import { pencilInImageFor } from '../../utils/pencilInImages';
import { SubstitutionProgress } from './SubstitutionProgress';
import { PlanHousehold } from '../../utils/waitingLines';

interface CustomMealCardProps {
  assignment: MealPlanAssignment;
  canModify: boolean;
  actionsDisabled: boolean;
  /** Opens the swap sheet to replace the penciled meal with a real recipe */
  onSwap: () => void;
  /** A swap for this day is in flight; the card shows it and locks actions */
  swapping?: boolean;
  /** Streamed AI-swap progress; cookbook swaps have none (bare spinner) */
  substitutionProgress?: SubstitutionGenerationProgress;
  /** Household from the plan's profile snapshot, for the progress copy */
  household?: PlanHousehold;
  onEdit: () => void;
  onDelete: () => void;
  /** Reorder grip rendered astride the top border, same as recipe cards */
  dragHandle?: ReactNode;
}

// Plan-grid card for a penciled-in meal (user-facing name for a custom meal).
// Mirrors the recipe card's anatomy — a 200px image with the day badge
// overlaid top-left, then a title row — so it reads as a sibling card and the
// image line stays level across a mixed row. Leftover row height (the grid
// equalizes rows) is whitespace below the content, same as any short card.
export const CustomMealCard = ({
  assignment,
  canModify,
  actionsDisabled,
  onSwap,
  swapping,
  substitutionProgress,
  household,
  onEdit,
  onDelete,
  dragHandle,
}: CustomMealCardProps) => {
  const meal = assignment.customMeal;
  if (!meal) return null;

  const { src, focusY } = pencilInImageFor(meal.name, getAssignmentMealType(assignment));

  const card = (
    <Card withBorder h="100%" radius="lg" style={{ position: 'relative' }}>
      {/* Same treatment as the recipe card while its swap runs: full-card
          overlay, with the streaming progress plate when an AI swap reports */}
      {swapping && (
        <LoadingOverlay
          visible
          // Above the card's own content but below Mantine drawers and modals
          // (200): the default 400 painted over open drawers
          zIndex={50}
          overlayProps={substitutionProgress ? { blur: 2 } : undefined}
          loaderProps={
            substitutionProgress
              ? {
                  children: (
                    <SubstitutionProgress
                      progress={substitutionProgress}
                      household={household}
                      mealType={getAssignmentMealType(assignment)}
                    />
                  ),
                }
              : { size: 'sm' }
          }
        />
      )}
      <Stack gap="md" h="100%">
        <Box
          style={{
            position: 'relative',
            borderRadius: 8,
            overflow: 'hidden',
            // Same height as the recipe card image so the photo band lines up
            // across a mixed row.
            height: 200,
            flexShrink: 0,
          }}
        >
          <img
            src={src}
            alt={meal.name}
            draggable={false}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: `center ${focusY}%`,
              // Penciled days are provisional, not planned dishes; mute the
              // photo and veil it (below) so they never read as recipe cards.
              filter: 'saturate(0.7)',
              userSelect: 'none',
            }}
          />
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.32)',
              pointerEvents: 'none',
            }}
          />
          <Badge
            variant="filled"
            color="orange"
            style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
          >
            {assignment.plannedDay}
          </Badge>
        </Box>

        <Box>
          <Group gap="xs" wrap="nowrap" align="flex-start" justify="space-between">
            <Title order={4}>{meal.name}</Title>
            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
              <Badge size="sm" variant="light" color="grape" tt="none" fw={500}>
                Penciled in
              </Badge>
              {canModify && (
                <Menu position="bottom-end" withinPortal>
                  <Menu.Target>
                    {/* No color prop: inherits the theme primary like the
                        recipe card kebab */}
                    <ActionIcon variant="subtle" size="lg" aria-label="Meal options">
                      <IconDotsVertical size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconRefresh size={14} stroke={1.5} />}
                      onClick={onSwap}
                      disabled={actionsDisabled || swapping}
                    >
                      {swapping ? 'Finding new recipe...' : 'Swap this meal...'}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconPencil size={14} stroke={1.5} />}
                      onClick={onEdit}
                      disabled={actionsDisabled || swapping}
                    >
                      Edit penciled-in meal
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconTrash size={14} stroke={1.5} />}
                      color="red"
                      onClick={onDelete}
                      disabled={actionsDisabled || swapping}
                    >
                      Remove day
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Group>

          {meal.note && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {meal.note}
            </Text>
          )}
        </Box>

        {meal.ingredients.length > 0 ? (
          <Group gap={6}>
            {meal.ingredients.map((line, index) => (
              <Badge key={index} variant="light" color="gray" tt="none" fw={500}>
                {line}
              </Badge>
            ))}
          </Group>
        ) : (
          !meal.note && (
            <Text size="sm" c="dimmed">
              Nothing needed from the store.
            </Text>
          )
        )}
      </Stack>
    </Card>
  );

  // The handle straddles the top border, so it anchors to a relative wrapper
  // rather than the Card itself, whose overflow: hidden would clip the half
  // above the border (same pattern as RecipeCard).
  return dragHandle ? (
    <Box style={{ position: 'relative', height: '100%' }}>
      {card}
      {dragHandle}
    </Box>
  ) : (
    card
  );
};
